/**
 * The tooltip provider: whether this is a phone-sized or touch screen (so a
 * tooltip can become a sheet), which tooltip is open (only one at a time),
 * and the theme to draw it in.
 *
 * Mounted only by features/plant-list TemperatureForecast, around its
 * readings; App's TooltipProvider is Radix's (components/ui/tooltip), a
 * different thing with the same name. Read with useTooltip.
 *
 * @module TooltipContext
 */
import React, { useEffect, useMemo, useState } from 'react';
import { TOOLTIP_CONFIG } from '../config/tooltipConfig';
import { useMediaQuery } from '@/shared/hooks/useMediaQuery';
import { useTheme } from '@/shared/hooks/useTheme';
import { isActualTouchDevice } from '../hooks/useTooltip';
import { TooltipContext } from './tooltipContextDefinition';
import { debugLog } from '@/config/config';

/**
 * Props for the TooltipProvider component
 * @interface
 * @property {React.ReactNode} children - Child components that will have access to tooltip context
 */
interface TooltipProviderProps {
  /** What the tooltips are in. */
  children: React.ReactNode;
}

/**
 * Provider component for tooltip functionality
 * Handles device detection, responsive behavior, and tooltip state management
 * 
 * Features:
 * - Automatic device capability detection (mobile/touch)
 * - Responsive behavior based on screen size
 * - Theme-aware tooltip rendering
 * - Centralized tooltip state management
 * - Debug logging for device detection
 * 
 * @component
 * @example
 * ```tsx
 * <TooltipProvider>
 *   <App />
 * </TooltipProvider>
 * ```
 */
export function TooltipProvider({ children }: TooltipProviderProps): JSX.Element {
  const [activeTooltipId, setActiveTooltipId] = useState<string | null>(null);
  const isMobile = useMediaQuery(`(max-width: ${TOOLTIP_CONFIG.MOBILE_BREAKPOINT}px)`);
  const [isTouch, setIsTouch] = useState(isActualTouchDevice);
  const { theme } = useTheme();

  useEffect(() => {
    // Log initial state
    debugLog('Tooltip', '[TooltipProvider] Initial state:', {
      isMobile,
      isTouch,
      width: window.innerWidth,
      breakpoint: TOOLTIP_CONFIG.MOBILE_BREAKPOINT,
      userAgent: window.navigator.userAgent
    });

    /** Re-reads whether the device is touch, after a rotation or a resize. */
    const updateTouchDetection = () => {
      const newIsTouch = isActualTouchDevice();
      if (newIsTouch !== isTouch) {
        debugLog('Tooltip', '[TooltipProvider] Touch detection changed:', { 
          previous: isTouch, 
          new: newIsTouch 
        });
        setIsTouch(newIsTouch);
      }
    };

    window.addEventListener('orientationchange', updateTouchDetection);
    window.addEventListener('resize', updateTouchDetection);

    return () => {
      window.removeEventListener('orientationchange', updateTouchDetection);
      window.removeEventListener('resize', updateTouchDetection);
    };
  }, [isMobile, isTouch]);

  // One object per change, not per render: every tooltip on the page reads it.
  const value = useMemo(() => ({
    isMobile,
    isTouch,
    activeTooltipId,
    setActiveTooltipId,
    // A cast: the app's theme may be 'grow' or 'fieldnotes' as well.
    theme: theme as 'light' | 'dark'
  }), [isMobile, isTouch, activeTooltipId, theme]);

  return (
    <TooltipContext.Provider value={value}>
      {children}
    </TooltipContext.Provider>
  );
} 